<template>
  <div class="permission-page card border-0 shadow-none rounded-3 mb-40">
    <div class="card-body p-xl-40 p-lg-30 p-20">
      <div class="permission-header-card">
        <div class="permission-header-card__badge">
          <i class="ri-shield-keyhole-line"></i>
          <span>{{ $t("group_permission_page.edit_badge") }}</span>
        </div>
        <h4 class="permission-header-card__title">{{ $t("group_permission_page.edit_title") }}</h4>
        <p class="permission-header-card__text">
          {{ $t("group_permission_page.edit_description") }}
        </p>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="row g-4">
          <div class="col-md-6">
            <div class="field-group">
              <label class="field-label">{{ $t("group_permission_page.role_name") }}</label>
              <input
                v-model="role.role_name"
                type="text"
                class="field-input"
                :placeholder="$t('group_permission_page.role_name_placeholder')"
                required
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="field-group">
              <label class="field-label">{{ $t("group_permission_page.description_label") }}</label>
              <input
                v-model="role.description"
                type="text"
                class="field-input"
                :placeholder="$t('group_permission_page.description_placeholder')"
              />
            </div>
          </div>
        </div>

        <div class="permission-toolbar">
          <div>
            <h5 class="permission-toolbar__title">{{ $t("group_permission_page.permission_access") }}</h5>
            <p class="permission-toolbar__hint">
              {{ $t("group_permission_page.permission_access_hint") }}
            </p>
          </div>
          <label class="permission-toggle" for="select_all">
            <input
              id="select_all"
              class="form-check-input"
              type="checkbox"
              :checked="isAllSelected"
              @change="toggleAll"
            />
            <span>{{ $t("group_permission_page.select_all_permissions") }}</span>
          </label>
        </div>

        <div class="row g-4">
          <div
            v-for="(group, gIndex) in localizedPermissionGroups"
            :key="gIndex"
            class="col-xl-4 col-lg-6"
          >
            <div class="permission-card">
              <div class="permission-card__header">
                <h6>{{ group.name }}</h6>
              </div>
              <div class="permission-card__body">
                <label
                  v-for="(perm, pIndex) in group.permissions"
                  :key="pIndex"
                  class="permission-item"
                  :for="`permission-${gIndex}-${perm.key}`"
                >
                  <input
                    :id="`permission-${gIndex}-${perm.key}`"
                    v-model="role[perm.key]"
                    class="form-check-input"
                    type="checkbox"
                    :true-value="1"
                    :false-value="0"
                  />
                  <span>{{ perm.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4 pt-2">
          <button class="btn style-one" type="submit" :disabled="isSubmitting">
            <i class="ri-save-3-line"></i>
            {{ isSubmitting ? $t("group_permission_page.updating") : $t("group_permission_page.update_role") }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import rolePermissionService, {
  type PermissionStructureGroup,
  type RolePermission,
} from "@/services/role_permission";
import { useCurrentUser } from "@/composables/useCurrentUser";
import Swal from 'sweetalert2';
import { localizePermissionStructure } from "@/utils/rolePermissionI18n";

type EditableRole = Partial<RolePermission> &
  Record<string, string | number | boolean | undefined> & {
    role_name: string;
    description: string;
    status: number;
  };

export default defineComponent({
  name: "EditGroupPermission",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { t, te } = useI18n();
    const { fetchCurrentUser } = useCurrentUser();
    const isSubmitting = ref(false);
    const permissionGroups = ref<PermissionStructureGroup[]>([]);

    const role = ref<EditableRole>({
      role_name: "",
      description: "",
      status: 1,
    });

    const localizedPermissionGroups = computed(() =>
      localizePermissionStructure(permissionGroups.value, t, te),
    );

    const fetchRole = async () => {
      try {
        const id = Number(route.params.id);
        const [structure, roleData] = await Promise.all([
          rolePermissionService.getPermissionStructure(),
          rolePermissionService.getRole(id)
        ]);
        
        permissionGroups.value = structure;
        role.value = roleData;
        
        // Ensure all keys are initialized and booleans converted to 1/0
        permissionGroups.value.forEach(group => {
          group.permissions.forEach((perm) => {
            // Convert boolean to 1/0 if needed
            if (typeof role.value[perm.key] === 'boolean') {
              role.value[perm.key] = role.value[perm.key] ? 1 : 0;
            }
            
            if (role.value[perm.key] === undefined) {
              role.value[perm.key] = 0;
            }
          });
        });
      } catch (error) {
        console.error("Error fetching role:", error);
        Swal.fire({
          icon: 'error',
          title: t("group_permission_page.error_title"),
          text: t("group_permission_page.load_role_failed_text")
        });
        router.push('/group-permission');
      }
    };

    onMounted(() => {
      fetchRole();
    });

    const isAllSelected = computed(() => {
      if (permissionGroups.value.length === 0) return false;
      for (const group of permissionGroups.value) {
        for (const perm of group.permissions) {
          if (!role.value[perm.key]) return false;
        }
      }
      return true;
    });

    const toggleAll = (e: Event) => {
      const checked = (e.target as HTMLInputElement).checked;
      const val = checked ? 1 : 0;
      permissionGroups.value.forEach(group => {
        group.permissions.forEach((perm) => {
          role.value[perm.key] = val;
        });
      });
    };

    const handleSubmit = async () => {
      isSubmitting.value = true;
      try {
        const id = Number(route.params.id);
        await rolePermissionService.updateRole(id, role.value);
        await fetchCurrentUser(true);
        Swal.fire({
          icon: 'success',
          title: t("group_permission_page.success_title"),
          text: t("group_permission_page.role_updated_successfully"),
          timer: 1500,
          showConfirmButton: false
        });
        router.push('/group-permission');
      } catch (error) {
        console.error("Error updating role:", error);
        Swal.fire({
          icon: 'error',
          title: t("group_permission_page.error_title"),
          text: t("group_permission_page.failed_to_update_role")
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      role,
      permissionGroups,
      localizedPermissionGroups,
      isSubmitting,
      handleSubmit,
      isAllSelected,
      toggleAll
    };
  },
});
</script>

<style scoped>
.permission-page {
  background: #ffffff;
}

.permission-header-card {
  margin-bottom: 32px;
  padding: 28px 30px;
  border: 1px solid #eef1f7;
  border-radius: 24px;
  background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
}

.permission-header-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 999px;
  background: #eef2ff;
  color: #5c61f2;
  font-size: 15px;
  font-weight: 600;
}

.permission-header-card__badge i {
  font-size: 18px;
}

.permission-header-card__title {
  margin: 18px 0 10px;
  color: #25324b;
  font-size: 22px;
  font-weight: 700;
}

.permission-header-card__text {
  margin: 0;
  max-width: 720px;
  color: #6b7280;
  font-size: 16px;
  line-height: 1.7;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.field-label {
  color: #25324b;
  font-size: 15px;
  font-weight: 600;
}

.field-input {
  width: 100%;
  height: 58px;
  padding: 0 18px;
  border: 1px solid #d9e2f2;
  border-radius: 18px;
  background: #f9fbff;
  color: #25324b;
  font-size: 15px;
  transition: all 0.2s ease;
}

.field-input:focus {
  border-color: #5c61f2;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(92, 97, 242, 0.12);
  outline: none;
}

.permission-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 34px 0 22px;
  flex-wrap: wrap;
}

.permission-toolbar__title {
  margin: 0 0 6px;
  color: #25324b;
  font-size: 18px;
  font-weight: 700;
}

.permission-toolbar__hint {
  margin: 0;
  color: #7a8599;
  font-size: 14px;
}

.permission-toggle {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 12px 16px;
  border: 1px solid #dbe3f2;
  border-radius: 16px;
  background: #ffffff;
  color: #25324b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.permission-card {
  height: 100%;
  border: 1px solid #e8edf7;
  border-radius: 22px;
  background: #ffffff;
  overflow: hidden;
}

.permission-card__header {
  padding: 16px 20px;
  background: #2f3d58;
}

.permission-card__header h6 {
  margin: 0;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
}

.permission-card__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px 20px;
}

.permission-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  color: #25324b;
  font-size: 15px;
  cursor: pointer;
}

.permission-item .form-check-input,
.permission-toggle .form-check-input {
  margin-top: 0;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  border-color: #5c61f2;
  box-shadow: none;
}

.permission-item .form-check-input:checked,
.permission-toggle .form-check-input:checked {
  background-color: #5c61f2;
  border-color: #5c61f2;
}

@media only screen and (max-width: 767px) {
  .permission-header-card {
    padding: 22px 18px;
    border-radius: 20px;
  }

  .permission-header-card__title {
    font-size: 20px;
  }

  .permission-header-card__text {
    font-size: 14px;
  }

  .field-input {
    height: 54px;
    border-radius: 16px;
  }

  .permission-card__header {
    padding: 14px 16px;
  }

  .permission-card__body {
    padding: 16px;
  }
}
</style>
